import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Paper,
  Badge,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  Search,
  Add,
  Person,
  TrendingUp,
  Star,
  FilterList,
  Download,
  Refresh,
  UploadFile,
  ContactPage,
  TableView,
} from '@mui/icons-material';
import DataTable, { Column } from '../components/common/DataTable';
import WhatsAppLink from '../components/common/WhatsAppLink';
import CustomerForm from '../components/customers/CustomerForm';
import CustomerDetail from '../components/customers/CustomerDetail';
import { useAppDispatch } from '../hooks/redux';
import { addNotification } from '../store/slices/uiSlice';
import { format } from 'date-fns';
import {
  customerService,
  Customer as ApiCustomer,
  CustomerAnalytics,
  ImportCustomersResult,
} from '../services/customerService';

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  dateOfBirth?: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
  };
  preferences: {
    skinType: 'oily' | 'dry' | 'combination' | 'sensitive' | '';
    hairType: 'straight' | 'wavy' | 'curly' | 'coily' | '';
    allergies: string[];
    notes: string;
  };
  loyaltyPoints: number;
  totalSpent: number;
  lastVisit?: string;
  visitCount: number;
  status: 'active' | 'inactive' | 'blocked';
  createdAt: string;
}

function mapApiCustomerToFrontend(c: ApiCustomer): Customer {
  const addr = c.address || {};
  const prefs = c.preferences || {};
  return {
    id: c.id,
    firstName: c.firstName,
    lastName: c.lastName,
    email: c.email ?? undefined,
    phone: c.phone,
    dateOfBirth: c.dateOfBirth ?? undefined,
    address: {
      street: addr.street ?? '',
      city: addr.city ?? '',
      state: addr.state ?? '',
      pincode: addr.pincode ?? '',
    },
    preferences: {
      skinType: (prefs.skinType as any) ?? '',
      hairType: (prefs.hairType as any) ?? '',
      allergies: prefs.allergies ?? [],
      notes: prefs.notes ?? '',
    },
    loyaltyPoints: c.loyaltyPoints ?? 0,
    totalSpent: Number(c.totalSpent ?? 0),
    lastVisit: c.lastVisit ? new Date(c.lastVisit).toISOString().split('T')[0] : undefined,
    visitCount: c.visitCount ?? 0,
    status: (c.status?.toLowerCase() ?? 'active') as Customer['status'],
    createdAt: c.createdAt,
  };
}

const columns: Column[] = [
  {
    id: 'name',
    label: 'Customer',
    format: (_value: any, row: Customer) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          sx={{
            bgcolor: '#E8B4B8',
            color: 'white',
            width: 40,
            height: 40,
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          {row.firstName.charAt(0)}{row.lastName.charAt(0)}
        </Avatar>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {row.firstName} {row.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {row.id}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    id: 'contact',
    label: 'Contact',
    format: (_value: any, row: Customer) => (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            {row.phone}
          </Typography>
          <WhatsAppLink
            phone={row.phone}
            message={`Hi ${row.firstName}, this is from your salon.`}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {row.email || 'No email'}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'visitCount',
    label: 'Visits',
    align: 'center',
    format: (value: number) => (
      <Chip
        label={`${value} visits`}
        size="small"
        sx={{
          bgcolor: value > 10 ? 'rgba(72, 187, 120, 0.1)' : 'rgba(237, 137, 54, 0.1)',
          color: value > 10 ? '#48BB78' : '#ED8936',
          fontWeight: 600,
          border: `1px solid ${value > 10 ? 'rgba(72, 187, 120, 0.3)' : 'rgba(237, 137, 54, 0.3)'}`,
        }}
      />
    ),
  },
  {
    id: 'totalSpent',
    label: 'Total Spent',
    format: (value: number) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          ₹{value.toLocaleString()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Lifetime value
        </Typography>
      </Box>
    ),
  },
  {
    id: 'lastVisit',
    label: 'Last Visit',
    format: (value: string) => {
      const date = value ? new Date(value) : null;
      const isRecent = date && (Date.now() - date.getTime()) < 30 * 24 * 60 * 60 * 1000; // 30 days
      
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            {value ? format(date!, 'MMM dd, yyyy') : 'Never'}
          </Typography>
          {isRecent && (
            <Chip
              label="Recent"
              size="small"
              sx={{
                bgcolor: 'rgba(72, 187, 120, 0.1)',
                color: '#48BB78',
                fontSize: '0.7rem',
                height: 20,
                mt: 0.5,
              }}
            />
          )}
        </Box>
      );
    },
  },
  {
    id: 'status',
    label: 'Status',
    format: (value: string) => (
      <Chip
        label={value.charAt(0).toUpperCase() + value.slice(1)}
        color={value === 'active' ? 'success' : value === 'inactive' ? 'warning' : 'error'}
        size="small"
        sx={{
          fontWeight: 600,
          textTransform: 'capitalize',
        }}
      />
    ),
  },
];

const Customers: React.FC = () => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportCustomersResult | null>(null);
  const [analytics, setAnalytics] = useState<CustomerAnalytics | null>(null);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format: 'vcf' | 'csv') => {
    setExportAnchor(null);
    setExporting(true);
    try {
      const blob = await customerService.exportCustomers(format, {
        search: debouncedSearch || undefined,
        status: statusFilter !== 'all' ? statusFilter.toUpperCase() : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const dateStamp = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers-${dateStamp}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      dispatch(addNotification({
        type: 'success',
        message: `Customer ${format.toUpperCase()} downloaded — ${
          format === 'vcf' ? 'import into your WhatsApp Business App' : 'open in Excel'
        }`,
      }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err instanceof Error ? err.message : 'Export failed',
      }));
    } finally {
      setExporting(false);
    }
  };

  const refreshAnalytics = async () => {
    const result = await customerService.getAnalytics();
    if (result) setAnalytics(result);
  };

  useEffect(() => {
    refreshAnalytics();
  }, []);

  const fetchCustomers = async () => {
    setFetching(true);
    try {
      const result = await customerService.getCustomers({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        status: statusFilter !== 'all' ? statusFilter.toUpperCase() : undefined,
      });
      const mapped = (result.customers || []).map(mapApiCustomerToFrontend);
      setCustomers(mapped);
      setTotalCount(result.pagination?.total ?? mapped.length);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
      dispatch(addNotification({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to load customers',
      }));
      setCustomers([]);
      setTotalCount(0);
    } finally {
      setFetching(false);
    }
  };

  // Debounce search input so we don't hammer the API on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Reset to first page whenever filters change — otherwise we may request
  // a page that no longer exists for the new result set.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter, rowsPerPage]);

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  const handleView = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDetailOpen(true);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormOpen(true);
  };

  const handleDelete = async (customer: Customer) => {
    try {
      await customerService.deleteCustomer(customer.id);
      dispatch(addNotification({
        type: 'success',
        message: `Customer ${customer.firstName} ${customer.lastName} deleted successfully`,
      }));
      setDetailOpen(false);
      setSelectedCustomer(null);
      await Promise.all([fetchCustomers(), refreshAnalytics()]);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to delete customer',
      }));
    }
  };

  const handleFormSubmit = async (customerData: any) => {
    setLoading(true);
    try {
      const payload = {
        firstName: customerData.firstName,
        lastName: customerData.lastName,
        email: customerData.email || undefined,
        phone: customerData.phone,
        dateOfBirth: customerData.dateOfBirth || undefined,
        address: customerData.address,
        preferences: customerData.preferences,
      };
      if (editingCustomer) {
        await customerService.updateCustomer(editingCustomer.id, payload);
        dispatch(addNotification({ type: 'success', message: 'Customer updated successfully' }));
      } else {
        await customerService.createCustomer(payload);
        dispatch(addNotification({ type: 'success', message: 'Customer created successfully' }));
      }
      setFormOpen(false);
      setEditingCustomer(null);
      await Promise.all([fetchCustomers(), refreshAnalytics()]);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to save customer',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingCustomer(null);
    setFormOpen(true);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    const fileName = file.name.toLowerCase();
    const isVCard = fileName.endsWith('.vcf');
    const isCsv = fileName.endsWith('.csv');

    if (!isVCard && !isCsv) {
      dispatch(addNotification({
        type: 'error',
        message: 'Please select a .vcf or .csv contacts file',
      }));
      return;
    }

    setImporting(true);
    try {
      const fileText = await file.text();
      const result = isCsv
        ? await customerService.importCsvContacts(fileText)
        : await customerService.importVCardContacts(fileText);
      setImportResult(result);
      dispatch(addNotification({
        type: 'success',
        message: `Imported ${result.created} customers from ${file.name}`,
      }));
      await fetchCustomers();
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to import contacts',
      }));
    } finally {
      setImporting(false);
    }
  };

  const handleEditFromDetail = () => {
    if (selectedCustomer) {
      setEditingCustomer(selectedCustomer);
      setDetailOpen(false);
      setFormOpen(true);
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', p: 2 }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".vcf,.csv,text/vcard,text/x-vcard,text/csv"
        onChange={handleImportFile}
        style={{ display: 'none' }}
      />

      {/* Enhanced Header Section with Animation */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 3, 
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(232, 180, 184, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  color: '#5C2A30',
                  mb: 1,
                }}
              >
                Customer Management
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(92, 42, 48, 0.85)',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                }}
              >
                Build lasting relationships with your valued customers
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Import .vcf or .csv contacts" arrow>
                <span>
                  <Button
                    variant="contained"
                    startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <UploadFile />}
                    onClick={handleImportClick}
                    disabled={importing || fetching}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.55)',
                      color: '#5C2A30',
                      fontWeight: 800,
                      px: 3,
                      py: 1.5,
                      borderRadius: 3,
                      fontSize: '1rem',
                      textTransform: 'none',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.6)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 35px rgba(0, 0, 0, 0.2)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(92, 42, 48, 0.55)',
                        bgcolor: 'rgba(255, 255, 255, 0.32)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {importing ? 'Importing...' : 'Import Contacts'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Refresh Data" arrow>
                <IconButton 
                  onClick={fetchCustomers}
                  disabled={fetching}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export contacts (for WhatsApp broadcast lists)" arrow>
                <span>
                  <IconButton
                    onClick={(e) => setExportAnchor(e.currentTarget)}
                    disabled={exporting || fetching}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.4)',
                      color: '#5C2A30',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(92, 42, 48, 0.18)',
                      width: 48,
                      height: 48,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.6)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {exporting ? <CircularProgress size={20} color="inherit" /> : <Download />}
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                anchorEl={exportAnchor}
                open={!!exportAnchor}
                onClose={() => setExportAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => void handleExport('vcf')}>
                  <ListItemIcon>
                    <ContactPage fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Export as VCF"
                    secondary="Import into WhatsApp Business App"
                  />
                </MenuItem>
                <MenuItem onClick={() => void handleExport('csv')}>
                  <ListItemIcon>
                    <TableView fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Export as CSV"
                    secondary="Open in Excel / Google Sheets"
                  />
                </MenuItem>
                {(debouncedSearch || statusFilter !== 'all') && (
                  <Box sx={{ px: 2, py: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Exporting current filter only
                    </Typography>
                  </Box>
                )}
              </Menu>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddNew}
                sx={{
                  bgcolor: 'white',
                  color: '#2D3748', // Darker text color for better visibility
                  fontWeight: 800, // Increased font weight
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  border: '2px solid rgba(255, 255, 255, 0.9)', // Added border for definition
                  backdropFilter: 'blur(10px)',
                  textShadow: 'none', // Remove any text shadow
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    color: '#1A202C', // Even darker on hover
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.2)',
                    border: '2px solid white',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '& .MuiButton-startIcon': {
                    color: '#2D3748', // Ensure icon is also dark
                  },
                }}
              >
                Add New Customer
              </Button>
            </Box>
          </Box>
        </Box>
        
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
      </Paper>

      {/* Enhanced Summary Cards with Better Animations */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={300} style={{ transitionDelay: '100ms' }}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(102, 126, 234, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Total Customers
                    </Typography>
                    <Typography 
                      variant="h3" 
                      component="div" 
                      sx={{ 
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2.5rem',
                      }}
                    >
                      {analytics?.totalCustomers ?? totalCount}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <TrendingUp sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        +12% this month
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)', 
                      width: 70, 
                      height: 70,
                      color: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Person sx={{ fontSize: 35 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={400} style={{ transitionDelay: '200ms' }}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(240, 147, 251, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(240, 147, 251, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Active Customers
                    </Typography>
                    <Typography 
                      variant="h3" 
                      component="div" 
                      sx={{ 
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2.5rem',
                      }}
                    >
                      {analytics?.activeCustomers ?? 0}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Star sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        98% retention rate
                      </Typography>
                    </Box>
                  </Box>
                  <Badge 
                    badgeContent="Active" 
                    color="success"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        fontWeight: 700,
                      }
                    }}
                  >
                    <Avatar 
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.2)', 
                        width: 70, 
                        height: 70,
                        color: 'white',
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                      }}
                    >
                      <Star sx={{ fontSize: 35 }} />
                    </Avatar>
                  </Badge>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={500} style={{ transitionDelay: '300ms' }}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(79, 172, 254, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(79, 172, 254, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Total Revenue
                    </Typography>
                    <Typography 
                      variant="h3" 
                      component="div" 
                      sx={{ 
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2rem',
                      }}
                    >
                      ₹{(customers.reduce((sum, c) => sum + c.totalSpent, 0) / 1000).toFixed(0)}K
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <TrendingUp sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        +18% growth
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)', 
                      width: 70, 
                      height: 70,
                      color: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Typography variant="h3" sx={{ fontWeight: 800 }}>₹</Typography>
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={600} style={{ transitionDelay: '400ms' }}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(250, 112, 154, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(250, 112, 154, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Average Visits
                    </Typography>
                    <Typography 
                      variant="h3" 
                      component="div" 
                      sx={{ 
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2.5rem',
                      }}
                    >
                      {Math.round(customers.reduce((sum, c) => sum + c.visitCount, 0) / customers.length)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <TrendingUp sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        High loyalty score
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)', 
                      width: 70, 
                      height: 70,
                      color: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Typography variant="h3" sx={{ fontWeight: 800 }}>#</Typography>
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
      </Grid>

      {/* Enhanced Filters Section with Glassmorphism */}
      <Fade in timeout={700} style={{ transitionDelay: '500ms' }}>
        <Card 
          sx={{ 
            mb: 3,
            mt: 2, // Added top margin for padding
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 180, 184, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
            },
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #E8B4B8 0%, #D4969B 100%)',
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(232, 180, 184, 0.3)',
                }}
              >
                <FilterList sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                  Search & Filter
                </Typography>
                <Typography variant="body2" sx={{ color: '#2D3748', fontWeight: 500 }}>
                  Find and organize your customers efficiently
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ p: 2, bgcolor: 'rgba(232, 180, 184, 0.05)', borderRadius: 3, border: '1px solid rgba(232, 180, 184, 0.1)' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    placeholder="Search by name, phone, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          '& fieldset': {
                            borderColor: '#E8B4B8',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'white',
                          '& fieldset': {
                            borderColor: '#E8B4B8',
                            borderWidth: '2px',
                          },
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#E8B4B8', fontSize: 24 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#E8B4B8' }}>Status Filter</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status Filter"
                      onChange={(e) => setStatusFilter(e.target.value)}
                      sx={{
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E8B4B8',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E8B4B8',
                            borderWidth: '2px',
                          },
                        },
                      }}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="blocked">Blocked</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Chip
                      label={`${totalCount} Results Found`}
                      sx={{
                        background: 'linear-gradient(135deg, #E8B4B8 0%, #D4969B 100%)',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        height: 36,
                        boxShadow: '0 4px 12px rgba(232, 180, 184, 0.3)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 16px rgba(232, 180, 184, 0.4)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    />
                    {searchTerm && (
                      <Chip 
                        label="Clear Search"
                        onClick={() => setSearchTerm('')}
                        onDelete={() => setSearchTerm('')}
                        color="secondary"
                        variant="outlined"
                        sx={{
                          borderColor: '#E8B4B8',
                          color: '#E8B4B8',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: 'rgba(232, 180, 184, 0.1)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {/* Enhanced Customer Table with Glassmorphism */}
      <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
        <Card 
          sx={{ 
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 180, 184, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                }}
              >
                <Person sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                  Customer Directory
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete list of your valued customers
                </Typography>
              </Box>
            </Box>
            
            <DataTable
              columns={columns}
              rows={customers}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              loading={loading || fetching}
            />
          </Box>
        </Card>
      </Fade>

      {/* Customer Form Dialog */}
      <CustomerForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingCustomer(null);
        }}
        onSubmit={handleFormSubmit}
        customer={editingCustomer}
        loading={loading || fetching}
      />

      {/* Customer Detail Dialog */}
      <CustomerDetail
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedCustomer(null);
        }}
        customer={selectedCustomer}
        onEdit={handleEditFromDetail}
      />

      <Dialog open={Boolean(importResult)} onClose={() => setImportResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Contact Import Summary</DialogTitle>
        <DialogContent>
          {importResult && (
            <Box sx={{ display: 'grid', gap: 1.5, mt: 1 }}>
              <Typography variant="body1">
                Total contacts found: <strong>{importResult.totalParsed}</strong>
              </Typography>
              <Typography variant="body1">
                New customers created: <strong>{importResult.created}</strong>
              </Typography>
              <Typography variant="body1">
                Already in CRM: <strong>{importResult.skippedExisting}</strong>
              </Typography>
              <Typography variant="body1">
                Duplicate contacts in file: <strong>{importResult.skippedDuplicates}</strong>
              </Typography>
              <Typography variant="body1">
                Invalid contacts skipped: <strong>{importResult.skippedInvalid}</strong>
              </Typography>
              {importResult.invalidRecords.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Some contacts were skipped because they did not have a valid phone number.
                </Typography>
              )}
              {importResult.skippedRecords.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Skipped Contacts
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 260,
                      overflow: 'auto',
                      border: '1px solid rgba(232, 180, 184, 0.25)',
                      borderRadius: 2,
                      bgcolor: 'rgba(232, 180, 184, 0.04)',
                    }}
                  >
                    {importResult.skippedRecords.map((record, index) => (
                      <Box
                        key={`${record.phone || record.name || 'skipped'}-${index}`}
                        sx={{
                          p: 1.5,
                          borderBottom:
                            index === importResult.skippedRecords.length - 1
                              ? 'none'
                              : '1px solid rgba(232, 180, 184, 0.18)',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {record.name || 'Unnamed contact'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {record.phone || 'No phone number'}
                        </Typography>
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          {record.reason}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Showing up to 100 skipped contacts.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;